import cn from 'classnames';
import { ChangeEvent, FC, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import { Button, Modal, Textarea } from '@/shared/ui';

import { templateEditorMessages } from '../../config/i18n';

import styles from './SourceDocumentDialog.module.scss';

type SourceDocumentDialogProps = {
  isOpen: boolean;
  sourceDocument?: string;
  readOnly?: boolean;
  onSave?: (sourceDocument: string) => void;
  onClose: () => void;
};

export const SourceDocumentDialog: FC<SourceDocumentDialogProps> = (props) => {
  const {
    isOpen,
    sourceDocument = '',
    readOnly = false,
    onSave,
    onClose,
  } = props;

  const { formatMessage } = useIntl();

  const [content, setContent] = useState(sourceDocument);

  const handleContentChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      setContent(event.target.value);
    },
    [],
  );

  const handleSave = useCallback(() => {
    onSave?.(content);
    onClose();
  }, [content, onSave, onClose]);

  return (
    <Modal
      data-component="source-document-dialog"
      isOpen={isOpen}
      title={formatMessage(templateEditorMessages.sourceDocumentTitle)}
      onCancel={onClose}
      footer={
        <div className={styles.footer}>
          <Button variant="secondary" onClick={onClose}>
            {formatMessage(templateEditorMessages.cancel)}
          </Button>
          {!readOnly && (
            <Button onClick={handleSave}>
              {formatMessage(templateEditorMessages.save)}
            </Button>
          )}
        </div>
      }
    >
      <div className={styles.container}>
        {readOnly ? (
          <pre className={cn('p3', styles.readOnlyContent)}>
            {content || '\u2014'}
          </pre>
        ) : (
          <Textarea
            value={content}
            isElastic
            rows={10}
            onChange={handleContentChange}
            placeholder={formatMessage(
              templateEditorMessages.sourceDocumentPlaceholder,
            )}
          />
        )}
      </div>
    </Modal>
  );
};
